import { useState, useEffect, useCallback } from 'react';
import { X, ArrowLeft, MapPin, Clock, User, AlertTriangle, Eye, Brain, Mail, Pencil, Check, Activity, Download } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { theme } from '../../../theme';
import { OutcomeBadge } from './OutcomeBadge';
import { StatusBadge } from './StatusBadge';
import { NotesSection } from './NotesSection';
import { StatusTracker } from './StatusTracker';
import { formatDateTime, formatDuration } from '../hooks/useRelativeTime';
import { fetchIncidentDetail, updateIncidentFields } from '../services/assessmentHistoryService';
import { RED_FLAGS, OBSERVABLE_SIGNS, SELF_SYMPTOMS, EMOTION_SYMPTOMS, THINKING_SYMPTOMS, AWARENESS_QUESTIONS, OUTCOMES } from '../../sideline-assessment/data/decisionTree';
import type { IncidentWithDetails, CaseStatus, IncidentNote, StatusHistoryEntry } from '../types/assessmentHistory.types';

type AssessmentDetailDrawerProps = {
  incidentId: string | null;
  currentUserId: string;
  canEditStatus: boolean;
  onClose: () => void;
};

// Sport label lookup
const SPORT_LABELS: Record<string, string> = {
  rugby: 'Rugby', afl: 'AFL', soccer: 'Soccer', basketball: 'Basketball',
  skateboarding: 'Skateboarding', mtb: 'MTB', bmx: 'BMX', cricket: 'Cricket',
  hockey: 'Hockey', netball: 'Netball', tennis: 'Tennis', judo: 'Judo', other: 'Other',
};

// Resolve selected IDs to labels
const resolveLabels = (ids: string[], items: { id: string; label: string }[]) =>
  ids.map((id) => items.find((item) => item.id === id)?.label ?? id);

// Findings section — only renders if items exist
const FindingsGroup = ({
  title,
  icon,
  items,
  accentColor,
}: {
  title: string;
  icon: React.ReactNode;
  items: string[];
  accentColor: string;
}) => {
  if (items.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className={`text-xs font-bold uppercase tracking-wider ${accentColor}`}>
          {title} ({items.length})
        </span>
      </div>
      <ul className="space-y-1.5 ml-1">
        {items.map((label) => (
          <li key={label} className="flex items-start gap-2 text-sm">
            <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${accentColor.replace('text-', 'bg-')}`} />
            <span className={`${theme.text.heading} font-medium`}>{label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Inline editable text field
const EditableField = ({
  value,
  placeholder,
  onSave,
}: {
  value: string;
  placeholder: string;
  onSave: (val: string) => Promise<void>;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (draft.trim() === value) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(draft.trim());
      setIsEditing(false);
    } catch {
      // revert on failure
      setDraft(value);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 200))}
          className={`${theme.input.base} text-sm py-1 px-2 flex-1`}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') { setDraft(value); setIsEditing(false); }
          }}
          disabled={isSaving}
        />
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="p-1 rounded-md dark:hover:bg-white/10 hover:bg-gray-100 transition-colors"
        >
          <Check className="w-3.5 h-3.5 text-emerald-500" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 group">
      <span className={`${theme.text.heading} font-medium text-sm`}>
        {value || <span className={theme.text.muted}>{placeholder}</span>}
      </span>
      <button
        onClick={() => { setDraft(value); setIsEditing(true); }}
        className="p-0.5 rounded opacity-0 group-hover:opacity-100 dark:hover:bg-white/10 hover:bg-gray-100 transition-all"
      >
        <Pencil className={`w-3 h-3 ${theme.text.muted}`} />
      </button>
    </div>
  );
};

function loadLogoDataUrl(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

type DrawerDocWithGState = {
  GState: (opts: { opacity: number }) => unknown;
  setGState: (gs: unknown) => void;
  saveGraphicsState: () => void;
  restoreGraphicsState: () => void;
};
function drawPdfWatermark(doc: jsPDF, pageW: number, pageH: number, darkLogoDataUrl: string) {
  const wmW = pageW * 0.55;
  const wmH = wmW * (1279 / 1888);
  const wmX = (pageW - wmW) / 2;
  const wmY = (pageH - wmH) / 2;
  const d = doc as unknown as DrawerDocWithGState;
  d.saveGraphicsState();
  d.setGState(d.GState({ opacity: 0.07 }));
  doc.addImage(darkLogoDataUrl, 'PNG', wmX, wmY, wmW, wmH);
  d.restoreGraphicsState();
}

const generateAssessmentPdf = async (detail: IncidentWithDetails, redFlagLabels: string[], signLabels: string[], symptomLabels: string[]) => {
  const [logo, darkLogo] = await Promise.all([
    loadLogoDataUrl('/sy-logo-light.png'),
    loadLogoDataUrl('/sy-logo-dark.png'),
  ]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const metadata = (detail.metadata ?? {}) as Record<string, unknown>;
  const assessmentData = detail.assessment_data;
  const outcomeData = detail.outcome ? OUTCOMES[detail.outcome] : null;
  const sportId = metadata.sport as string | undefined;
  const sportLabel = sportId ? (SPORT_LABELS[sportId] ?? sportId) : null;
  const assessorName = metadata.assessor_name as string | undefined;

  const pageW = 210;
  const pageH = 297;
  const margin = 16;
  const contentW = pageW - margin * 2;

  // Draw watermark first so it sits behind all content
  if (darkLogo) drawPdfWatermark(doc, pageW, pageH, darkLogo);

  // ── Header bar ───────────────────────────────────────────────────────────
  doc.setFillColor(10, 25, 41); // SY Navy
  doc.rect(0, 0, pageW, 28, 'F');

  // SY logo — top-LEFT of header (ratio 1888:1279 ≈ 1.476:1)
  if (logo) {
    const logoH = 12;
    const logoW = logoH * (1888 / 1279);
    doc.addImage(logo, 'PNG', margin, 7, logoW, logoH);
  }

  doc.setTextColor(32, 201, 151); // SY Teal
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('CHARLIE — CONCUSSION CO-PILOT', margin, 9);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.text('Concussion Assessment Report', margin, 18);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text(`Ref: ${detail.incident_ref}  ·  ${formatDateTime(detail.created_at)}`, margin, 24);

  let y = 38;

  // ── Outcome badge ─────────────────────────────────────────────────────────
  if (outcomeData) {
    // Parse hex color to RGB
    const hex = outcomeData.color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    doc.setFillColor(r, g, b);
    doc.roundedRect(margin, y, 60, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(outcomeData.title.toUpperCase(), margin + 3, y + 5.5);
    y += 14;
  }

  // ── Summary grid ─────────────────────────────────────────────────────────
  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, contentW, 32, 2, 2, 'FD');
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');

  const col1 = margin + 4;
  const col2 = margin + contentW / 2 + 2;

  const drawSummaryField = (label: string, value: string, x: number, fy: number) => {
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(label.toUpperCase(), x, fy);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(value || '—', x, fy + 4.5);
  };

  drawSummaryField('Person Assessed', detail.subject_name ?? 'Unknown', col1, y + 6);
  drawSummaryField('Date', formatDateTime(detail.created_at), col2, y + 6);
  drawSummaryField('Sport', sportLabel ?? '—', col1, y + 18);
  if (assessmentData?.started_at && assessmentData?.completed_at) {
    drawSummaryField('Duration', formatDuration(assessmentData.started_at, assessmentData.completed_at), col2, y + 18);
  } else {
    drawSummaryField('Assessor', assessorName ?? 'Anonymous', col2, y + 18);
  }
  y += 38;

  // Location
  if (detail.geolocation?.address) {
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('LOCATION', margin, y);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const locLines = doc.splitTextToSize(detail.geolocation.address, contentW);
    doc.text(locLines, margin, y + 4);
    y += 4 + locLines.length * 4 + 4;
  }

  // ── Outcome description ──────────────────────────────────────────────────
  if (outcomeData?.description) {
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    const descLines = doc.splitTextToSize(outcomeData.description, contentW - 8);
    const descH = descLines.length * 4 + 10;
    doc.roundedRect(margin, y, contentW, descH, 2, 2, 'FD');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(descLines, margin + 4, y + 7);
    y += descH + 6;
  }

  // ── Findings helper ──────────────────────────────────────────────────────
  const drawFindingsSection = (title: string, items: string[], accentHex: string) => {
    if (items.length === 0) return;
    const hex2 = accentHex.replace('#', '');
    const r2 = parseInt(hex2.substring(0, 2), 16);
    const g2 = parseInt(hex2.substring(2, 4), 16);
    const b2 = parseInt(hex2.substring(4, 6), 16);
    doc.setTextColor(r2, g2, b2);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(`${title} (${items.length})`, margin, y);
    y += 5;
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    items.forEach((item) => {
      doc.setFillColor(r2, g2, b2);
      doc.circle(margin + 1.5, y - 1.5, 1, 'F');
      const lines = doc.splitTextToSize(item, contentW - 8);
      doc.text(lines, margin + 5, y);
      y += lines.length * 4 + 1;
    });
    y += 4;
  };

  if (redFlagLabels.length > 0 || signLabels.length > 0 || symptomLabels.length > 0) {
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('FINDINGS', margin, y);
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, y + 1.5, margin + contentW, y + 1.5);
    y += 6;
    drawFindingsSection('Red Flags', redFlagLabels, '#ef4444');
    drawFindingsSection('Observable Signs', signLabels, '#f59e0b');
    drawFindingsSection('Symptoms', symptomLabels, '#eab308');
  } else {
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text('No red flags, observable signs, or symptoms were detected.', margin, y);
    y += 8;
  }

  // ── Disclaimer ───────────────────────────────────────────────────────────
  // Push disclaimer to near bottom of page
  const disclaimerY = Math.max(y + 6, 255);
  doc.setFillColor(255, 251, 235);
  doc.setDrawColor(253, 230, 138);
  doc.roundedRect(margin, disclaimerY, contentW, 16, 2, 2, 'FD');
  doc.setTextColor(146, 64, 14);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  const disclaimer = 'Important: Charlie is an information tool — not a medical device. This assessment does not replace professional medical evaluation. All suspected concussions must be assessed by a qualified medical professional. Based on CRT6 and Australian Institute of Sport concussion guidelines.';
  const dLines = doc.splitTextToSize(disclaimer, contentW - 6);
  doc.text(dLines, margin + 3, disclaimerY + 5);

  // ── Footer ───────────────────────────────────────────────────────────────
  doc.setTextColor(148, 163, 184);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Charlie — Safeguarding You\'s Concussion Co-Pilot  ·  safeguardingyou.ai', margin, 290);

  doc.save(`charlie-assessment-${detail.incident_ref}.pdf`);
};

export const AssessmentDetailDrawer = ({
  incidentId,
  currentUserId,
  canEditStatus,
  onClose,
}: AssessmentDetailDrawerProps) => {
  const [detail, setDetail] = useState<IncidentWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Load detail when incidentId changes
  useEffect(() => {
    if (!incidentId) {
      setDetail(null);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchIncidentDetail(incidentId);
        setDetail(data);
      } catch (err) {
        console.error('Failed to load incident detail:', err);
        setError('Could not load assessment details');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [incidentId]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (incidentId) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [incidentId, onClose]);

  const handleNoteAdded = useCallback((note: IncidentNote) => {
    setDetail((prev) => prev ? { ...prev, notes: [...prev.notes, note] } : prev);
  }, []);

  const handleStatusChanged = useCallback((newStatus: CaseStatus, entry: StatusHistoryEntry) => {
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            current_status: newStatus,
            status_history: [...prev.status_history, entry],
          }
        : prev
    );
  }, []);

  const handleUpdateSubjectName = useCallback(async (name: string) => {
    if (!detail) return;
    await updateIncidentFields(detail.id, { subject_name: name || null });
    setDetail((prev) => prev ? { ...prev, subject_name: name || null } : prev);
  }, [detail]);

  const handleDownloadPdf = useCallback(async () => {
    if (!detail) return;
    setIsDownloadingPdf(true);
    const assessmentData = detail.assessment_data;
    const allSymptoms = [...SELF_SYMPTOMS, ...EMOTION_SYMPTOMS, ...THINKING_SYMPTOMS];
    const redFlagLabels = resolveLabels(assessmentData?.selected_red_flags ?? [], RED_FLAGS);
    const signLabels = resolveLabels(assessmentData?.selected_observable_signs ?? [], OBSERVABLE_SIGNS);
    const symptomLabels = resolveLabels(assessmentData?.selected_self_symptoms ?? [], allSymptoms);
    await generateAssessmentPdf(detail, redFlagLabels, signLabels, symptomLabels);
    setIsDownloadingPdf(false);
  }, [detail]);

  const handleUpdateContactEmail = useCallback(async (email: string) => {
    if (!detail) return;
    const existing = (detail.metadata as Record<string, unknown>) || {};
    await updateIncidentFields(detail.id, {
      metadata: { ...existing, contact_email: email || null },
    });
    setDetail((prev) => {
      if (!prev) return prev;
      const meta = (prev.metadata as Record<string, unknown>) || {};
      return { ...prev, metadata: { ...meta, contact_email: email || null } };
    });
  }, [detail]);

  if (!incidentId) return null;

  const isOpen = !!incidentId;
  const assessmentData = detail?.assessment_data;
  const metadata = (detail?.metadata ?? {}) as Record<string, unknown>;
  const outcomeData = detail?.outcome ? OUTCOMES[detail.outcome] : null;

  // Resolve selected items to readable labels
  const ALL_SYMPTOMS = [...SELF_SYMPTOMS, ...EMOTION_SYMPTOMS, ...THINKING_SYMPTOMS];
  const redFlagLabels = resolveLabels(assessmentData?.selected_red_flags ?? [], RED_FLAGS);
  const signLabels = resolveLabels(assessmentData?.selected_observable_signs ?? [], OBSERVABLE_SIGNS);
  const symptomLabels = resolveLabels(assessmentData?.selected_self_symptoms ?? [], ALL_SYMPTOMS);
  const awarenessAnswers = (assessmentData?.awareness_answers ?? {}) as Record<string, 'normal' | 'impaired'>;
  const awarenessEntries = AWARENESS_QUESTIONS.filter((q) => awarenessAnswers[q.id]).map((q) => ({ label: q.label, answer: awarenessAnswers[q.id] }));
  const hasFindings = redFlagLabels.length > 0 || signLabels.length > 0 || symptomLabels.length > 0 || awarenessEntries.length > 0;

  const sportId = metadata.sport as string | undefined;
  const sportLabel = sportId ? (SPORT_LABELS[sportId] ?? sportId) : null;
  const assessorName = metadata.assessor_name as string | undefined;
  const contactEmail = metadata.contact_email as string | undefined;
  const emailSentAt = metadata.email_sent_at as string | undefined;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[480px] z-50 dark:bg-sy-navy bg-white shadow-2xl dark:shadow-black/40 transform transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full overflow-y-auto">
          <div className="p-6">
            {/* Close / Back button + Download PDF */}
            <div className="flex items-center justify-between mb-5">
              <button onClick={onClose} className={`${theme.button.back} sm:hidden`}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={onClose} className={`${theme.button.ghost} hidden sm:flex items-center gap-1`}>
                <X className="w-4 h-4" /> Close
              </button>
              {detail && (
                <button
                  onClick={handleDownloadPdf}
                  disabled={isDownloadingPdf}
                  className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border ${theme.border.base} ${theme.button.ghost} disabled:opacity-50`}
                >
                  {isDownloadingPdf ? (
                    <div className={`w-3.5 h-3.5 border-2 border-sy-teal/30 border-t-sy-teal rounded-full ${theme.anim.spin}`} />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  <span>Download PDF</span>
                </button>
              )}
            </div>

            {/* Loading state */}
            {isLoading && (
              <div className={theme.status.loading}>
                <div className={`w-5 h-5 border-2 border-sy-teal/30 border-t-sy-teal rounded-full ${theme.anim.spin}`} />
                <span className={`text-sm ${theme.text.muted}`}>Loading details...</span>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className={theme.status.error}>
                <p className={`text-sm font-medium ${theme.text.heading}`}>{error}</p>
              </div>
            )}

            {/* Content */}
            {detail && !isLoading && (
              <div className="space-y-6">
                {/* Header: ref + name + badges */}
                <div>
                  <p className={`font-mono text-xs ${theme.text.muted} mb-1`}>
                    {detail.incident_ref}
                  </p>
                  <div className="mb-3">
                    <EditableField
                      value={detail.subject_name ?? ''}
                      placeholder="Add player name"
                      onSave={handleUpdateSubjectName}
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <OutcomeBadge outcome={detail.outcome} size="md" />
                    <StatusBadge status={detail.current_status} />
                  </div>
                </div>

                {/* Summary section */}
                <div>
                  <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${theme.border.base}`}>
                    <Clock className={`w-3.5 h-3.5 ${theme.text.muted}`} />
                    <h4 className={`text-xs font-bold uppercase tracking-widest ${theme.text.muted}`}>
                      Summary
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {sportLabel && (
                      <div>
                        <p className={`text-[10px] uppercase tracking-wider ${theme.text.muted} mb-0.5`}>Sport</p>
                        <p className={`text-sm font-medium ${theme.text.heading}`}>{sportLabel}</p>
                      </div>
                    )}
                    {assessmentData?.started_at && assessmentData?.completed_at && (
                      <div>
                        <p className={`text-[10px] uppercase tracking-wider ${theme.text.muted} mb-0.5`}>Duration</p>
                        <p className={`text-sm font-medium ${theme.text.heading}`}>
                          {formatDuration(assessmentData.started_at, assessmentData.completed_at)}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className={`text-[10px] uppercase tracking-wider ${theme.text.muted} mb-0.5`}>Date</p>
                      <p className={`text-sm font-medium ${theme.text.heading}`}>
                        {formatDateTime(detail.created_at)}
                      </p>
                    </div>
                    {detail.geolocation?.address && (
                      <div className="col-span-2">
                        <p className={`text-[10px] uppercase tracking-wider ${theme.text.muted} mb-0.5`}>Location</p>
                        <p className={`text-sm font-medium ${theme.text.heading} flex items-center gap-1`}>
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          {detail.geolocation.address}
                        </p>
                      </div>
                    )}
                    {assessorName && (
                      <div>
                        <p className={`text-[10px] uppercase tracking-wider ${theme.text.muted} mb-0.5`}>Assessor</p>
                        <p className={`text-sm font-medium ${theme.text.heading} flex items-center gap-1`}>
                          <User className="w-3 h-3 flex-shrink-0" />
                          {assessorName}
                        </p>
                      </div>
                    )}
                    {!assessorName && (
                      <div>
                        <p className={`text-[10px] uppercase tracking-wider ${theme.text.muted} mb-0.5`}>Assessor</p>
                        <p className={`text-sm font-medium ${theme.text.heading} flex items-center gap-1`}>
                          <User className="w-3 h-3 flex-shrink-0" />
                          {detail.user_id ? 'Team member' : 'Anonymous'}
                        </p>
                      </div>
                    )}
                    <div className="col-span-2">
                      <p className={`text-[10px] uppercase tracking-wider ${theme.text.muted} mb-0.5`}>Contact Email</p>
                      <div className="flex items-center gap-1.5">
                        <Mail className={`w-3 h-3 flex-shrink-0 ${theme.text.muted}`} />
                        <EditableField
                          value={contactEmail ?? ''}
                          placeholder="Add contact email"
                          onSave={handleUpdateContactEmail}
                        />
                      </div>
                      {emailSentAt && (
                        <p className={`text-[10px] ${theme.text.muted} mt-0.5 ml-4.5`}>
                          Report emailed {formatDateTime(emailSentAt)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Outcome description */}
                  {outcomeData && (
                    <div className={`mt-3 p-3 rounded-xl ${theme.card.muted} border ${theme.border.base}`}>
                      <p className="text-xs font-semibold mb-1" style={{ color: outcomeData.color }}>
                        {outcomeData.title}
                      </p>
                      <p className={`text-xs ${theme.text.body} leading-relaxed`}>
                        {outcomeData.description}
                      </p>
                    </div>
                  )}
                </div>

                {/* Findings — only selected items */}
                <div>
                  <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${theme.border.base}`}>
                    <Activity className={`w-3.5 h-3.5 ${theme.text.muted}`} />
                    <h4 className={`text-xs font-bold uppercase tracking-widest ${theme.text.muted}`}>
                      Findings
                    </h4>
                  </div>

                  {hasFindings ? (
                    <div className="space-y-4">
                      <FindingsGroup
                        title="Red Flags"
                        icon={<AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                        items={redFlagLabels}
                        accentColor="text-red-500"
                      />
                      <FindingsGroup
                        title="Visible Clues"
                        icon={<Eye className="w-3.5 h-3.5 dark:text-amber-400 text-amber-600" />}
                        items={signLabels}
                        accentColor="dark:text-amber-400 text-amber-600"
                      />
                      <FindingsGroup
                        title="Symptoms"
                        icon={<Brain className="w-3.5 h-3.5 dark:text-yellow-400 text-yellow-600" />}
                        items={symptomLabels}
                        accentColor="dark:text-yellow-400 text-yellow-600"
                      />
                      {awarenessEntries.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Activity className="w-3.5 h-3.5 dark:text-violet-400 text-violet-600" />
                            <span className="text-xs font-bold uppercase tracking-wider dark:text-violet-400 text-violet-600">
                              Awareness ({awarenessEntries.length}/{AWARENESS_QUESTIONS.length})
                            </span>
                          </div>
                          <ul className="space-y-1.5 ml-1">
                            {awarenessEntries.map(({ label, answer }) => (
                              <li key={label} className="flex items-center gap-2 text-sm">
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${answer === 'impaired' ? 'bg-red-500' : 'bg-emerald-500'}`} />
                                <span className={`${theme.text.heading} font-medium flex-1`}>{label}</span>
                                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                  answer === 'impaired'
                                    ? 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400'
                                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
                                }`}>
                                  {answer === 'impaired' ? 'Impaired' : 'Normal'}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className={`text-sm ${theme.text.muted} italic`}>
                      No red flags, visible clues, symptoms, or awareness issues detected.
                    </p>
                  )}
                </div>

                {/* Status Tracker */}
                <StatusTracker
                  incidentId={detail.id}
                  currentStatus={detail.current_status}
                  statusHistory={detail.status_history}
                  currentUserId={currentUserId}
                  canEditStatus={canEditStatus}
                  onStatusChanged={handleStatusChanged}
                />

                {/* Notes */}
                <NotesSection
                  incidentId={detail.id}
                  notes={detail.notes}
                  currentUserId={currentUserId}
                  onNoteAdded={handleNoteAdded}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
